import { createAsyncThunk } from "@reduxjs/toolkit";
import { setLoadedState, setLoggedState, setRestrictedState } from "@store/slices/loyaltyCoreSlice";
import type { IUserWithUUID } from "@store/slices/loyaltyCoreSlice/loyaltyCoreSlice.types";
import type { AppDispatch } from "@store/types";
import { isLoyaltyRestricted } from "@store/utils/isLoyaltyRestricted";

import { isServerSide } from "@jti/fe-common";
import { getUser } from "@jti/rest-api";
import { type IUser } from "@jti/rest-api-types";

import { handleUser } from "./handleUser";

type PageRunPayload = {
  name: string;
  isRestricted: boolean;
  isInEditor: boolean;
  isLoyaltyActive: boolean;
  isReferralActive: boolean;
};

export function isValidUser(user: IUser): user is IUserWithUUID {
  return !!(user.uuid && user.ciam_token);
}

export const pageRun = createAsyncThunk<void, PageRunPayload, { dispatch: AppDispatch }>(
  "loyaltyCore/pageRun",
  async ({ name, isRestricted, isInEditor, isLoyaltyActive, isReferralActive }, { dispatch }) => {
    const loyaltyStatus = isLoyaltyActive;
    const referralStatus = isReferralActive;
    const isReferralPage = name === "referral";
    const isLoyaltyOff = !isReferralPage && !loyaltyStatus;
    const isReferralOff = isReferralPage && !referralStatus;
    const isRasAndLoyaltyOn = isReferralPage && referralStatus && loyaltyStatus;
    const checkReferralAndLoyaltyStatus = (isLoyaltyOff || isReferralOff || isRasAndLoyaltyOn) && !isInEditor;

    if (checkReferralAndLoyaltyStatus) {
      if (!isServerSide()) {
        window.location.replace("/");
        throw new Error("Loyalty or Referral is not active");
      }
    }

    if (isRestricted) {
      try {
        const restricted = await isLoyaltyRestricted();
        dispatch(setRestrictedState(restricted));
      } catch (error) {
        console.error("Error checking if loyalty is restricted", error);
      }
    }

    dispatch(setLoadedState(true));

    const user = getUser();
    if (isValidUser(user)) {
      try {
        dispatch(setLoggedState(true));
        await dispatch(handleUser({ user, isLoyaltyActive: loyaltyStatus, isReferralActive: referralStatus }));
      } catch (error) {
        console.error("User validation failed", error);
      }
    }
  }
);
